/**
 * 🔴 A NUMBER TYPED THE WAY BANGLADESH TYPES IT — design-system plan DS-5 (F424, F511, F215).
 *
 * A quantity box that meets `১২০` hands `Number('১২০')` a NaN, and the till's pick, receive and transfer screens then
 * dropped the line without a word (the DS-5 survey found it). Every number a person types goes through these two:
 *
 * - `toLatinDigits(text)` — the Bengali digits ০–৯ become 0–9, nothing else changes. The same length in and out, so a
 *   text box can map as it is typed without moving the cursor.
 * - `readDecimal(text, { decimals, negative })` — the canonical decimal string (`"105000.5"`), or `null` when the text
 *   is not a number. It accepts what a person writes: spaces around it, Bengali digits, and grouping commas in lakh
 *   (`1,05,000`) or western (`105,000`) positions — never a comma anywhere else, because `5000,50` is a typo, not five
 *   thousand fifty. No exponents, no bare point, no more decimals than asked for. It never returns a `number`: the
 *   figure travels as the string the server's `NUMERIC` reads exactly.
 *
 * Money with a currency sign is the till's `parseTaka`, which reads `৳`, `Tk` and a trailing `/-` on top of this.
 */

const BENGALI_ZERO = 0x09e6;

export function toLatinDigits(text: string): string {
  return text.replace(/[০-৯]/g, (d) => String(d.charCodeAt(0) - BENGALI_ZERO));
}

/** Grouped in lakh (`1,05,000`, `12,34,567`) or western (`105,000`) positions, or not grouped at all. */
const WHOLE = /^(?:\d{1,3}(?:,\d{3})+|\d{1,2}(?:,\d{2})*,\d{3}|\d+)$/;

export interface ReadDecimalOptions {
  /** The most decimals accepted (default 4, the ledger's scale). 0 reads whole numbers only. */
  decimals?: number;
  /** Whether a leading minus is accepted (default false: a quantity or a price is never negative). */
  negative?: boolean;
}

export function readDecimal(text: string, { decimals = 4, negative = false }: ReadDecimalOptions = {}): string | null {
  let s = toLatinDigits(text).trim();
  let sign = '';
  if (s.startsWith('-') || s.startsWith('−')) {
    if (!negative) return null;
    sign = '-';
    s = s.slice(1).trimStart();
  }
  const point = s.indexOf('.');
  const whole = point < 0 ? s : s.slice(0, point);
  const fraction = point < 0 ? '' : s.slice(point + 1);
  if (!WHOLE.test(whole)) return null;
  if (point >= 0 && (fraction === '' || !/^\d+$/.test(fraction) || fraction.length > decimals)) return null;
  const digits = whole.replace(/,/g, '').replace(/^0+(?=\d)/, '');
  const trimmed = fraction.replace(/0+$/, '');
  const value = trimmed ? `${digits}.${trimmed}` : digits;
  return value === '0' ? '0' : `${sign}${value}`;
}

/**
 * `a` against `b` — negative, zero or positive, like a sort comparator — for two decimal strings, compared digit by
 * digit, never through a JavaScript number. `Number('99999999999999.9999')` is 100000000000000, so a range check done
 * with numbers let a figure past a `NUMERIC(18,4)` ceiling (DS-5 review). Either side may be written loosely; each is
 * read with `readDecimal` first, and a side that is not a number compares as neither above nor below.
 */
export function compareDecimal(a: string, b: string): number {
  const x = readDecimal(a, { decimals: 20, negative: true });
  const y = readDecimal(b, { decimals: 20, negative: true });
  if (x === null || y === null) return 0;
  const xn = x.startsWith('-');
  const yn = y.startsWith('-');
  if (xn !== yn) return xn ? -1 : 1;
  const [xi = '', xf = ''] = (xn ? x.slice(1) : x).split('.');
  const [yi = '', yf = ''] = (yn ? y.slice(1) : y).split('.');
  let c = xi.length - yi.length;
  if (c === 0) c = xi < yi ? -1 : xi > yi ? 1 : 0;
  if (c === 0) {
    const n = Math.max(xf.length, yf.length);
    const p = xf.padEnd(n, '0');
    const q = yf.padEnd(n, '0');
    c = p < q ? -1 : p > q ? 1 : 0;
  }
  return xn ? -c : c;
}
